<template>
  <div class="hiit-container">
    <div class="card">
      <h2>HIIT 计时器</h2>

      <div class="row">
        <label>工作时长(秒)</label>
        <input type="number" min="5" v-model.number="workSeconds" />
      </div>
      <div class="row">
        <label>休息时长(秒)</label>
        <input type="number" min="0" v-model.number="restSeconds" />
      </div>
      <div class="row">
        <label>准备倒计时(秒)</label>
        <input type="number" min="0" v-model.number="preparationSeconds" />
      </div>
      <div class="row">
        <label>组数</label>
        <input type="number" min="1" v-model.number="rounds" />
      </div>

      <div class="divider" />

      <div class="status">
        <div class="phase" :class="phaseClass">{{ phaseLabel }}</div>
        <div class="time">{{ formattedTimeLeft }}</div>
        <div class="round">第 {{ currentRound }} / {{ rounds }} 组</div>
      </div>

      <div class="progress">
        <div class="bar" :style="{ width: progressPct + '%' }" />
      </div>

      <div class="actions">
        <button @click="onStart" :disabled="running">开始</button>
        <button @click="onPause" :disabled="!running">暂停</button>
        <button @click="onReset">重置</button>
      </div>

      <div class="helper">
        <span>本组半程：{{ roundHalfNotified ? "已提醒" : "未提醒" }}</span>
      </div>

      <div class="divider" />

      <div class="presets">
        <div class="row">
          <label>预设名称</label>
          <input type="text" v-model="presetName" placeholder="如：Tabata 20/10 x 8" />
        </div>
        <div class="row">
          <button @click="savePreset">保存为预设</button>
          <button @click="overwritePreset" :disabled="!selectedPresetId">
            覆盖当前选中
          </button>
          <button @click="deletePreset" :disabled="!selectedPresetId">删除选中</button>
        </div>
        <div class="row">
          <label>我的预设 (最多5个)</label>
          <select v-model="selectedPresetId" @change="onPresetChange">
            <option :value="''">未选择</option>
            <option v-for="p in presets" :key="p.id" :value="p.id">
              {{ p.name }}（工{{ p.workSeconds }} / 休{{ p.restSeconds }} / 备{{
                p.preparationSeconds
              }}
              / 组{{ p.rounds }}）
            </option>
          </select>
          <button @click="applyPreset" :disabled="!selectedPresetId">应用</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HiiTPage",
  data() {
    return {
      workSeconds: 30,
      restSeconds: 15,
      preparationSeconds: 5,
      rounds: 8,

      running: false,
      isPrepare: false,
      isWork: true,
      currentRound: 1,
      timeLeft: 30,
      totalPlannedSecondsSnapshot: 0,
      elapsedTotalSeconds: 0,

      roundHalfNotified: false,
      countdownBeeped: {},
      intervalId: null,
      lastTickMs: 0,

      // 预设
      presets: [],
      presetName: "",
      selectedPresetId: "",
    };
  },
  computed: {
    phaseLabel() {
      if (this.isPrepare) return "准备";
      return this.isWork ? "工作" : "休息";
    },
    phaseClass() {
      if (this.isPrepare) return "prepare";
      return this.isWork ? "work" : "rest";
    },
    formattedTimeLeft() {
      const m = Math.floor(this.timeLeft / 60)
        .toString()
        .padStart(2, "0");
      const s = Math.floor(this.timeLeft % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    },
    // 计算总时长：每组工作都计入，休息在最后一组不计入
    totalPlannedSeconds() {
      return (
        this.rounds * this.workSeconds + Math.max(0, this.rounds - 1) * this.restSeconds
      );
    },
    progressPct() {
      const total = this.totalPlannedSecondsSnapshot || this.totalPlannedSeconds || 1;
      const pct = Math.min(100, Math.max(0, (this.elapsedTotalSeconds / total) * 100));
      return pct.toFixed(2);
    },
  },
  watch: {
    // 配置变更时，如果未运行，更新显示时间
    workSeconds() {
      if (!this.running && !this.isPrepare && this.isWork)
        this.timeLeft = this.workSeconds;
    },
    restSeconds() {
      if (!this.running && !this.isPrepare && !this.isWork)
        this.timeLeft = this.restSeconds;
    },
    rounds() {
      if (!this.running) this.currentRound = 1;
    },
  },
  mounted() {
    // 初始化显示
    this.timeLeft = this.workSeconds;
    this.loadPresets();
  },
  beforeUnmount() {
    this.clearTimer();
  },
  methods: {
    onStart() {
      if (this.running) return;
      // 启动前锁定一次总时长用于进度条（避免中途修改配置导致进度跳变）
      if (this.elapsedTotalSeconds === 0) {
        this.totalPlannedSecondsSnapshot = this.totalPlannedSeconds;
      }
      this.requestNotificationPermission();
      this.running = true;
      this.lastTickMs = performance.now();
      // 如果设置了准备倒计时，则先进入准备阶段
      if (
        this.preparationSeconds > 0 &&
        this.currentRound === 1 &&
        this.isWork &&
        !this.isPrepare &&
        this.elapsedTotalSeconds === 0
      ) {
        this.isPrepare = true;
        this.timeLeft = this.preparationSeconds;
        this.notify("准备开始");
        this.playPrepareTone();
      } else {
        // 直接开始当前阶段
        this.notify(this.isWork ? `第 ${this.currentRound} 组开始` : "休息开始");
        if (this.isWork) {
          this.playWorkStartTone();
        } else {
          this.playRestStartTone();
        }
      }
      this.countdownBeeped = {};
      this.roundHalfNotified = false;
      this.intervalId = window.setInterval(this.onTick, 200);
    },
    onPause() {
      this.running = false;
      this.clearTimer();
    },
    onReset() {
      this.clearTimer();
      this.running = false;
      this.isWork = true;
      this.currentRound = 1;
      this.timeLeft = this.workSeconds;
      this.elapsedTotalSeconds = 0;
      this.totalPlannedSecondsSnapshot = 0;
    },
    onTick() {
      const now = performance.now();
      const delta = Math.max(0, (now - this.lastTickMs) / 1000);
      this.lastTickMs = now;

      // 更新阶段剩余时间
      this.timeLeft = Math.max(0, this.timeLeft - delta);

      // 阶段结束前三秒倒计时提醒（3、2、1 秒各响一次）
      const secondsLeft = Math.ceil(this.timeLeft);
      if (
        !this.countdownBeeped[`${this.phaseLabel}-${secondsLeft}`] &&
        secondsLeft > 0 &&
        secondsLeft <= 3
      ) {
        this.countdownBeeped[`${this.phaseLabel}-${secondsLeft}`] = true;
        this.beep(700 - secondsLeft * 60, 90);
      }

      // 每组工作阶段半程提醒（只提醒一次/每组）
      if (!this.isPrepare && this.isWork && !this.roundHalfNotified) {
        if (this.timeLeft <= this.workSeconds / 2) {
          this.roundHalfNotified = true;
          this.notify("本组已到达一半，继续！");
          this.beep(820, 160);
        }
      }

      // 更新总进度（仅在运行状态累加）
      const totalForProgress =
        this.totalPlannedSecondsSnapshot || this.totalPlannedSeconds;
      const remainingForProgress = this.computeRemainingForProgress();
      const elapsed = Math.max(0, totalForProgress - remainingForProgress);
      this.elapsedTotalSeconds = elapsed;

      // 全程半程提醒已移除，仅保留每组半程提醒

      // 阶段结束切换
      if (this.timeLeft <= 0.01) {
        this.advancePhase();
      }
    },
    advancePhase() {
      if (this.isPrepare) {
        // 准备结束，进入第一组工作
        this.isPrepare = false;
        this.isWork = true;
        this.currentRound = 1;
        this.timeLeft = this.workSeconds;
        this.notify(`第 ${this.currentRound} 组开始`);
        this.playWorkStartTone();
        this.countdownBeeped = {};
        this.roundHalfNotified = false;
        return;
      }
      if (this.isWork) {
        // 从工作切到休息（除最后一组外）
        if (this.currentRound < this.rounds && this.restSeconds > 0) {
          this.isWork = false;
          this.timeLeft = this.restSeconds;
          this.notify("休息开始");
          this.playRestStartTone();
          this.countdownBeeped = {};
        } else {
          // 最后一组工作结束 = 训练完成
          this.finishWorkout();
        }
      } else {
        // 休息结束，进入下一组工作
        this.isWork = true;
        this.currentRound += 1;
        this.timeLeft = this.workSeconds;
        this.notify(`第 ${this.currentRound} 组开始`);
        this.playWorkStartTone();
        this.countdownBeeped = {};
        this.roundHalfNotified = false;
      }
    },
    finishWorkout() {
      this.notify("训练完成！干得漂亮！");
      // 完成音：上升三音
      this.beepSequence([
        { f: 523, d: 100 },
        { f: 659, d: 100 },
        { f: 784, d: 200 },
      ]);
      this.onPause();
    },
    computeRemainingForProgress() {
      // 进度计算基于：当前已完成时间 + 剩余时间
      // 总时长定义：rounds*work + (rounds-1)*rest
      let remaining = 0;
      // 当前阶段剩余（准备阶段不计入总训练进度）
      if (!this.isPrepare) remaining += this.timeLeft;
      // 后续阶段
      if (this.isPrepare) {
        // 仍未开始，后续为所有工作与休息
        const remainingWork = (this.rounds - (this.currentRound - 1)) * this.workSeconds;
        const remainingRest = Math.max(0, this.rounds - 1) * this.restSeconds;
        remaining += remainingWork + remainingRest;
      } else if (this.isWork) {
        // 当前是工作阶段
        const remainingWork = (this.rounds - this.currentRound) * this.workSeconds;
        const remainingRest =
          Math.max(0, this.rounds - this.currentRound) * this.restSeconds; // 后续每个工作后含一次休息
        remaining += remainingWork + remainingRest;
      } else {
        // 当前是休息阶段
        const remainingWork = (this.rounds - this.currentRound) * this.workSeconds;
        const remainingRest =
          Math.max(0, this.rounds - (this.currentRound + 0)) * this.restSeconds; // 本休息之后还可能有更多休息
        remaining += remainingWork + remainingRest;
      }
      return remaining;
    },
    requestNotificationPermission() {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    },
    notify(message) {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(message);
      } else {
        // 回退到页面可见提示
        try {
          // eslint-disable-next-line no-alert
          console.log(message);
        } catch (e) {
          // 保底：忽略
          // eslint-disable-next-line no-console
          console.warn(e);
        }
      }
    },
    clearTimer() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    // 简单提示音（WebAudio）
    beep(frequency = 600, durationMs = 120) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.type = "sine";
        o.frequency.value = frequency;
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
        o.start();
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
        o.stop(ctx.currentTime + durationMs / 1000 + 0.01);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    },
    beepSequence(steps) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        let t = ctx.currentTime;
        steps.forEach(({ f, d }) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.type = "sine";
          o.frequency.value = f;
          g.gain.setValueAtTime(0.001, t);
          g.gain.exponentialRampToValueAtTime(0.25, t + 0.01);
          o.start(t);
          g.gain.exponentialRampToValueAtTime(0.001, t + d / 1000);
          o.stop(t + d / 1000 + 0.01);
          t += d / 1000 + 0.02;
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    },
    // 区分提示音：
    playPrepareTone() {
      // 准备：短上升两音
      this.beepSequence([
        { f: 440, d: 100 },
        { f: 523, d: 140 },
      ]);
    },
    playWorkStartTone() {
      // 工作开始：更有激励的上升三音
      this.beepSequence([
        { f: 523, d: 100 },
        { f: 659, d: 120 },
        { f: 784, d: 160 },
      ]);
    },
    playRestStartTone() {
      // 休息开始：平缓下降两音
      this.beepSequence([
        { f: 659, d: 120 },
        { f: 440, d: 160 },
      ]);
    },
    // ===== 预设：本地存储 (最多 5 个) =====
    loadPresets() {
      try {
        const raw = localStorage.getItem("hiit_presets_v1");
        this.presets = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
      } catch (e) {
        this.presets = [];
      }
    },
    persistPresets() {
      try {
        localStorage.setItem("hiit_presets_v1", JSON.stringify(this.presets.slice(0, 5)));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    },
    currentConfig() {
      return {
        id: this.uuid(),
        name: this.presetName || `预设 ${new Date().toLocaleString()}`,
        workSeconds: Number(this.workSeconds) || 0,
        restSeconds: Number(this.restSeconds) || 0,
        preparationSeconds: Number(this.preparationSeconds) || 0,
        rounds: Number(this.rounds) || 1,
      };
    },
    savePreset() {
      const cfg = this.currentConfig();
      if (this.presets.length >= 5) {
        // 若已达上限，替换最旧（队头）
        this.presets.shift();
      }
      this.presets.push(cfg);
      this.selectedPresetId = cfg.id;
      this.persistPresets();
      this.notify("预设已保存");
    },
    overwritePreset() {
      if (!this.selectedPresetId) return;
      const idx = this.presets.findIndex((p) => p.id === this.selectedPresetId);
      if (idx === -1) return;
      const cfg = this.currentConfig();
      cfg.id = this.selectedPresetId; // 保留 id
      this.presets.splice(idx, 1, cfg);
      this.persistPresets();
      this.notify("预设已覆盖");
    },
    deletePreset() {
      if (!this.selectedPresetId) return;
      const idx = this.presets.findIndex((p) => p.id === this.selectedPresetId);
      if (idx === -1) return;
      this.presets.splice(idx, 1);
      this.selectedPresetId = "";
      this.persistPresets();
      this.notify("预设已删除");
    },
    onPresetChange() {
      // 选择变化时不立即应用，等待用户点“应用”
    },
    applyPreset() {
      if (!this.selectedPresetId) return;
      const p = this.presets.find((x) => x.id === this.selectedPresetId);
      if (!p) return;
      const wasRunning = this.running;
      if (wasRunning) this.onPause();
      this.workSeconds = Number(p.workSeconds) || 0;
      this.restSeconds = Number(p.restSeconds) || 0;
      this.preparationSeconds = Number(p.preparationSeconds) || 0;
      this.rounds = Number(p.rounds) || 1;
      // 重置显示
      this.onReset();
      if (wasRunning) this.onStart();
    },
    uuid() {
      // 简易 uuid
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
  },
};
</script>

<style scoped>
.hiit-container {
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5a4a3a;
  z-index: 10;
}
.card {
  width: 100%;
  max-width: 520px;
  background: #fdf6e9;
  border: 1px solid rgba(212, 165, 116, 0.4);
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 10px 30px rgba(139, 105, 20, 0.15);
}
.card h2 {
  margin: 0 0 12px 0;
  font-weight: 600;
  color: #8b6914;
}
.row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
}
.row label {
  width: 120px;
  opacity: 0.9;
}
.row input[type="number"] {
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(212, 165, 116, 0.5);
  background: rgba(255, 255, 255, 0.8);
  color: #5a4a3a;
}
.row input[type="text"],
.presets select {
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(212, 165, 116, 0.5);
  background: rgba(255, 255, 255, 0.8);
  color: #5a4a3a;
}
.divider {
  height: 1px;
  background: rgba(212, 165, 116, 0.4);
  margin: 12px 0;
}
.status {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  text-align: center;
  margin: 8px 0 12px 0;
}
.phase {
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
}
.phase.work {
  background: rgba(100, 180, 120, 0.2);
  border: 1px solid rgba(100, 180, 120, 0.5);
}
.phase.rest {
  background: rgba(100, 150, 200, 0.2);
  border: 1px solid rgba(100, 150, 200, 0.5);
}
.phase.prepare {
  background: rgba(220, 180, 80, 0.2);
  border: 1px solid rgba(220, 180, 80, 0.5);
}
.time {
  font-size: 28px;
  font-weight: 700;
  color: #8b6914;
}
.round {
  opacity: 0.9;
}
.progress {
  width: 100%;
  height: 10px;
  background: rgba(212, 165, 116, 0.2);
  border-radius: 999px;
  overflow: hidden;
}
.progress .bar {
  height: 100%;
  background: linear-gradient(90deg, #d4a574, #8b6914);
  width: 0%;
}
.actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}
.actions button {
  flex: 1;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(212, 165, 116, 0.5);
  background: rgba(212, 165, 116, 0.15);
  color: #5a4a3a;
  cursor: pointer;
}
.presets .row button {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(212, 165, 116, 0.5);
  background: rgba(212, 165, 116, 0.15);
  color: #5a4a3a;
  cursor: pointer;
}
.actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.helper {
  margin-top: 8px;
  opacity: 0.9;
}
</style>
